import { resolveRowFormulas } from './sheetFormula';
import { resolveHeaderReferences } from './sheetHeaderReferences';
export const normalizeSheetOptionSource = (optionSource) => {
  const sheetId = `${optionSource?.sheetId || ''}`.trim();
  const headerId = `${optionSource?.headerId || ''}`.trim();
  const sourceFilters = normalizeSheetOptionSourceFilters(
    optionSource?.sourceFilters
      || optionSource?.sourceFilter
      || optionSource?.filter
  );
  const filterLogic = `${optionSource?.filterLogic || ''}`.trim().toLowerCase() === 'or' ? 'or' : 'and';

  if (!sheetId || !headerId) return null;
  return sourceFilters.length ? { sheetId, headerId, sourceFilters, filterLogic } : { sheetId, headerId };
};

const normalizeSingleOptionValue = (value) => {
  if (value === null || value === undefined) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeSingleOptionValue(item));
  }

  if (typeof value === 'object') {
    const preferredValue = value?.value ?? value?.label ?? value?.name ?? value?.title;
    if (preferredValue !== null && preferredValue !== undefined && preferredValue !== value) {
      return normalizeSingleOptionValue(preferredValue);
    }
    return [];
  }

  const normalized = `${value}`.trim();
  if (!normalized) return [];

  return (normalized.includes(',') || normalized.includes('\n'))
    ? normalized.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean)
    : [normalized];
};

const normalizeValueAsOptions = (value) => Array.from(new Set(
  normalizeSingleOptionValue(value)
));

const normalizeManualOptions = (options = []) => normalizeValueAsOptions(Array.isArray(options) ? options : []);

const OPTION_RULE_OPERATORS = new Set([
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'greaterThan',
  'lessThan',
  'isEmpty',
  'isNotEmpty'
]);

const normalizeRuleValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => `${item ?? ''}`.trim())
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return `${value ?? ''}`;
};

export const normalizeSheetOptionSourceFilter = (sourceFilter, validHeaderIds = null) => {
  if (!sourceFilter || typeof sourceFilter !== 'object') return null;

  const sourceHeaderId = `${sourceFilter?.sourceHeaderId || ''}`.trim();
  const valueMode = `${sourceFilter?.valueMode || 'header'}`.trim() === 'static' ? 'static' : 'header';
  const headerId = `${sourceFilter?.headerId || ''}`.trim();
  const value = normalizeRuleValue(sourceFilter?.value);
  const operator = OPTION_RULE_OPERATORS.has(`${sourceFilter?.operator || ''}`.trim())
    ? `${sourceFilter.operator}`.trim()
    : 'equals';

  if (!sourceHeaderId) return null;
  if (valueMode === 'header') {
    if (!headerId) return null;
    if (validHeaderIds && !validHeaderIds.has(headerId)) return null;
  }

  return valueMode === 'static'
    ? { sourceHeaderId, valueMode, value, operator }
    : { sourceHeaderId, valueMode, headerId, operator };
};

export const normalizeSheetOptionSourceFilters = (sourceFilters, validHeaderIds = null) => {
  if (Array.isArray(sourceFilters)) {
    return sourceFilters
      .map((sourceFilter) => normalizeSheetOptionSourceFilter(sourceFilter, validHeaderIds))
      .filter(Boolean);
  }

  const normalizedSingle = normalizeSheetOptionSourceFilter(sourceFilters, validHeaderIds);
  return normalizedSingle ? [normalizedSingle] : [];
};

export const sanitizeHeaderConditionalOptions = (conditionalOptions = [], headers = [], currentHeaderId = '') => {
  const validHeaderIds = new Set(
    (Array.isArray(headers) ? headers : [])
      .map((header) => `${header?.id || ''}`.trim())
      .filter((headerId) => headerId && headerId !== currentHeaderId)
  );

  return (Array.isArray(conditionalOptions) ? conditionalOptions : [])
    .map((rule) => {
      const whenHeaderId = `${rule?.whenHeaderId || rule?.headerId || rule?.fieldId || ''}`.trim();
      const operator = OPTION_RULE_OPERATORS.has(`${rule?.operator || ''}`.trim())
        ? `${rule.operator}`.trim()
        : 'equals';
      if (!whenHeaderId || !validHeaderIds.has(whenHeaderId)) return null;

      const options = normalizeManualOptions(rule?.options || rule?.manualOptions || []);
      const optionSource = normalizeSheetOptionSource(rule?.optionSource);
      if (!options.length && !optionSource) return null;

      return {
        whenHeaderId,
        operator,
        value: normalizeRuleValue(rule?.value),
        options,
        optionSource: optionSource || undefined
      };
    })
    .filter(Boolean);
};

const evalConditionalRule = (operator, sourceValue, targetValue) => {
  const source = normalizeRuleValue(sourceValue);
  const target = normalizeRuleValue(targetValue);

  switch (operator) {
    case 'equals':
      return source === target;
    case 'notEquals':
      return source !== target;
    case 'contains':
      return source.toLowerCase().includes(target.toLowerCase());
    case 'notContains':
      return !source.toLowerCase().includes(target.toLowerCase());
    case 'greaterThan':
      return Number(source) > Number(target);
    case 'lessThan':
      return Number(source) < Number(target);
    case 'isEmpty':
      return source.trim() === '';
    case 'isNotEmpty':
      return source.trim() !== '';
    default:
      return false;
  }
};

const doesSourceRowMatchFilter = (row, sourceFilter, rowValues = {}, headers = [], currentHeaderId = '') => {
  if (!sourceFilter?.sourceHeaderId) return true;

  const sourceValue = row?.resolvedValues?.[sourceFilter.sourceHeaderId] ?? row?.values?.[sourceFilter.sourceHeaderId];
  const targetValue = sourceFilter.valueMode === 'static'
    ? resolveHeaderReferences(sourceFilter.value, headers, rowValues, currentHeaderId)
    : rowValues?.[sourceFilter.headerId];

  return evalConditionalRule(sourceFilter.operator || 'equals', sourceValue, targetValue);
};

const doesSourceRowMatchFilters = (row, sourceFilters = [], filterLogic = 'and', rowValues = {}, headers = [], currentHeaderId = '') => {
  const normalizedFilters = normalizeSheetOptionSourceFilters(sourceFilters);
  if (!normalizedFilters.length) return true;

  return (filterLogic === 'or' ? normalizedFilters.some : normalizedFilters.every)
    .call(normalizedFilters, (sourceFilter) => doesSourceRowMatchFilter(row, sourceFilter, rowValues, headers, currentHeaderId));
};

const getOptionSourceRows = (sourceData) => (
  Array.isArray(sourceData) ? sourceData : (Array.isArray(sourceData?.rows) ? sourceData.rows : [])
);

const getOptionSourceHeaders = (sourceData) => (
  Array.isArray(sourceData) ? [] : (Array.isArray(sourceData?.headers) ? sourceData.headers : [])
);

const getProfileOptionLabel = (header, row) => {
  const rowValues = row?.resolvedValues || row?.values || {};
  const labelHeaderId = `${header?.profileConfig?.labelHeaderId || ''}`.trim();
  if (labelHeaderId) {
    const labelValue = `${rowValues?.[labelHeaderId] ?? ''}`.trim();
    if (labelValue) return labelValue;
  }

  const rawValue = rowValues?.[header.id];
  return `${rawValue ?? ''}`.trim();
};

export const extractSheetColumnOptions = (sourceData = [], sourceHeaderId, optionSourceConfig = null, rowValues = {}, headers = [], currentHeaderId = '') => {
  const normalizedHeaderId = `${sourceHeaderId || ''}`.trim();
  if (!normalizedHeaderId) return [];
  const rows = getOptionSourceRows(sourceData);
  const sourceHeaders = getOptionSourceHeaders(sourceData);
  const sourceHeader = sourceHeaders.find((header) => `${header?.id || ''}`.trim() === normalizedHeaderId);
  const sourceSheetId = `${optionSourceConfig?.sheetId || ''}`.trim();
  const sourceFilters = Array.isArray(optionSourceConfig?.sourceFilters)
    ? optionSourceConfig.sourceFilters
    : normalizeSheetOptionSourceFilters(optionSourceConfig?.sourceFilter || optionSourceConfig?.filter || optionSourceConfig);
  const filterLogic = `${optionSourceConfig?.filterLogic || ''}`.trim().toLowerCase() === 'or' ? 'or' : 'and';

  return Array.from(new Set(
    rows
      .filter((row) => doesSourceRowMatchFilters(row, sourceFilters, filterLogic, rowValues, headers, currentHeaderId))
      .flatMap((row) => {
        const resolvedRowValues = row?.resolvedValues && typeof row.resolvedValues === 'object'
          ? row.resolvedValues
          : (
            sourceHeader && `${sourceHeader?.formula || ''}`.trim()
              ? resolveRowFormulas(
                  sourceHeaders,
                  row?.values || {},
                  sourceSheetId ? { [sourceSheetId]: sourceData } : {}
                )
              : null
          );

        if (sourceHeader?.type === 'profile') {
          const label = getProfileOptionLabel(
            sourceHeader,
            resolvedRowValues ? { ...row, resolvedValues: resolvedRowValues } : row
          );
          return label ? [label] : [];
        }
        const rawValue = resolvedRowValues?.[normalizedHeaderId] ?? row?.resolvedValues?.[normalizedHeaderId] ?? row?.values?.[normalizedHeaderId];
        if (Array.isArray(rawValue)) {
          return rawValue;
        }
        return rawValue === '' || rawValue === null || rawValue === undefined ? [] : [rawValue];
      })
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean)
  ));
};

const resolveOptionSet = (options = [], optionSource = null, optionSourceRowsBySheetId = {}, rowValues = {}, headers = [], currentHeaderId = '') => {
  const manualOptions = normalizeManualOptions(options)
    .flatMap((option) => normalizeValueAsOptions(resolveHeaderReferences(option, headers, rowValues, currentHeaderId)));
  if (!optionSource) return manualOptions;

  const sourceData = optionSourceRowsBySheetId?.[optionSource.sheetId] || { rows: [], headers: [] };
  const sourceHeaders = getOptionSourceHeaders(sourceData);
  const sourceHeader = sourceHeaders.find((header) => `${header?.id || ''}`.trim() === `${optionSource.headerId || ''}`.trim());
  const importedOptions = extractSheetColumnOptions(
    sourceData,
    optionSource.headerId,
    optionSource,
    rowValues,
    headers,
    currentHeaderId
  );
  const fallbackResolvedOptions = normalizeValueAsOptions(rowValues?.[optionSource.headerId]);
  const preferCurrentRowResolvedOptions = Boolean(
    sourceHeader &&
    fallbackResolvedOptions.length &&
    (
      sourceHeader?.type === 'list' ||
      sourceHeader?.type === 'multiselect' ||
      `${sourceHeader?.formula || ''}`.trim()
    )
  );

  const effectiveImportedOptions = importedOptions.length
    ? importedOptions
    : (
      getOptionSourceRows(sourceData).length === 0 &&
      getOptionSourceHeaders(sourceData).some((header) => `${header?.id || ''}`.trim() === `${optionSource.headerId || ''}`.trim())
    )
      ? fallbackResolvedOptions
      : [];

  return Array.from(new Set([
    ...manualOptions,
    ...(preferCurrentRowResolvedOptions ? fallbackResolvedOptions : effectiveImportedOptions)
  ]));
};

export const resolveHeaderOptions = (header, optionSourceRowsBySheetId = {}, rowValues = {}, headers = []) => {
  const referenceHeaders = Array.isArray(headers) && headers.length ? headers : [header].filter(Boolean);
  const directListHeaderId = `${header?.optionsFromHeaderId || ''}`.trim();
  if (directListHeaderId) {
    const directListOptions = normalizeValueAsOptions(rowValues?.[directListHeaderId]);
    if (directListOptions.length) return directListOptions;
  }

  const conditionalOptions = Array.isArray(header?.conditionalOptions) ? header.conditionalOptions : [];
  const matchingRules = conditionalOptions.filter((rule) => (
    evalConditionalRule(
      rule?.operator || 'equals',
      rowValues?.[rule.whenHeaderId],
      resolveHeaderReferences(rule?.value, referenceHeaders, rowValues, header?.id)
    )
  ));

  if (matchingRules.length) {
    return Array.from(new Set(
      matchingRules.flatMap((rule) => (
        resolveOptionSet(rule?.options || [], normalizeSheetOptionSource(rule?.optionSource), optionSourceRowsBySheetId, rowValues, referenceHeaders, header?.id)
      ))
    ));
  }

  const resolvedOptions = resolveOptionSet(
    header?.options || [],
    normalizeSheetOptionSource(header?.optionSource),
    optionSourceRowsBySheetId,
    rowValues,
    referenceHeaders,
    header?.id
  );

  if (resolvedOptions.length) return resolvedOptions;
  return normalizeValueAsOptions(rowValues?.[header?.id]);
};
